// @story HIR-HR7
// Data retention — the school's view of how long candidate data is kept, the
// two-key switch for automatic removal, the candidates coming up for removal,
// and the removal requests candidates have made (HIR-CN4) for HR to action.
import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function RetentionSettings() {
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [declining, setDeclining] = useState(null);
  const [reason, setReason] = useState('');

  const load = () => {
    setErr('');
    Promise.all([
      api.get('/retention/summary'),
      api.get('/retention/upcoming?limit=50'),
      api.get('/retention/requests'),
    ])
      .then(([s, u, r]) => {
        setSummary(s.data?.data || null);
        setUpcoming(u.data?.data || []);
        setRequests(r.data?.data || []);
      })
      .catch((e) => setErr(e.response?.data?.message || 'Could not load data retention.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleSchool = () => {
    const next = !summary.schoolSwitch;
    if (next && !window.confirm('Turn on automatic removal for this school? Candidates past their date will be erased everywhere, permanently. Only do this once the retention schedule has been confirmed by counsel.')) return;
    setSaving(true); setErr('');
    api.put('/school-settings', { retention: { autoDelete: next } })
      .then(load)
      .catch((e) => setErr(e.response?.data?.message || 'Could not save — try again.'))
      .finally(() => setSaving(false));
  };

  const decline = (id) => {
    api.post(`/retention/requests/${id}/decline`, { reason })
      .then(() => { setDeclining(null); setReason(''); load(); })
      .catch((e) => setErr(e.response?.data?.message || 'Could not decline the request.'));
  };

  if (loading) return <div className="text-gray-500 text-sm">Loading…</div>;
  if (!summary) return <div className="text-sm text-red-600">{err || 'Could not load data retention.'}</div>;

  const open = requests.filter((r) => r.status === 'open' || r.status === 'in_progress');
  const done = requests.filter((r) => r.status === 'erased' || r.status === 'declined');

  return (
    <div className="font-body">
      <h2 className="font-heading text-lg font-bold text-gray-900 mb-1">Data retention</h2>
      <p className="text-sm text-gray-500 mb-5">
        How long candidate data is kept, and requests from candidates to remove theirs.
      </p>
      {err && <div className="text-sm text-red-600 mb-3">{err}</div>}

      <div className="rounded-xl border border-gray-200 p-4">
        <div className="font-medium text-gray-900">Unsuccessful candidates: kept {summary.months} months</div>
        <div className="text-sm text-gray-500 mt-0.5 max-w-xl">
          The clock starts when a candidate is not taken forward, or when the role closes without an offer to them.
          Hired candidates are never removed by it — their record moves to People.
          Applications waiting in the General applications pool are kept {summary.months} months after the last activity on them.
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 text-center">
          <div className="rounded-lg bg-gray-50 p-3"><div className="text-xl font-bold text-gray-900">{summary.counts.tracked}</div><div className="text-xs text-gray-500">on the clock</div></div>
          <div className="rounded-lg bg-gray-50 p-3"><div className="text-xl font-bold text-gray-900">{summary.counts.next30}</div><div className="text-xs text-gray-500">due in the next 30 days</div></div>
          <div className="rounded-lg bg-gray-50 p-3"><div className="text-xl font-bold text-gray-900">{summary.counts.due}</div><div className="text-xs text-gray-500">past their date</div></div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4 mt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-medium text-gray-900">Automatic removal</div>
            <div className="text-sm text-gray-500 mt-0.5 max-w-xl">
              {summary.autoDelete
                ? 'On. Candidates past their date are erased everywhere — Hiring, the landing page, Messages and stored files.'
                : 'Off. Nothing is deleted automatically; each candidate shows the date they would be removed. Needs both this school\'s switch and the platform switch.'}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              This school: <strong>{summary.schoolSwitch ? 'on' : 'off'}</strong>
              {summary.schoolSwitchChangedBy ? ` (set by ${summary.schoolSwitchChangedBy}, ${fmt(summary.schoolSwitchChangedAt)})` : ''}
              {' · '}Platform: <strong>{summary.masterSwitch ? 'on' : 'off'}</strong>
            </div>
          </div>
          <button
            role="switch" aria-checked={summary.schoolSwitch} onClick={toggleSchool} disabled={saving}
            className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${summary.schoolSwitch ? 'bg-green-600' : 'bg-gray-300'} disabled:opacity-60`}
            title={summary.schoolSwitch ? 'On — click to turn off' : 'Off — click to turn on'}
          >
            <span className={`absolute top-0.5 ${summary.schoolSwitch ? 'left-[22px]' : 'left-0.5'} w-5 h-5 bg-white rounded-full shadow transition-all`} />
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4 mt-4">
        <div className="font-medium text-gray-900 mb-1">Removal requests from candidates</div>
        <div className="text-sm text-gray-500 mb-3">
          A candidate can ask from the careers page, confirmed with a code sent to the WhatsApp number on their application.
          Open the candidate to erase their data; or decline with a reason.
        </div>
        {open.length === 0 && <div className="text-sm text-gray-400 italic">No open requests.</div>}
        {open.map((r) => (
          <div key={r.id} className="flex items-start justify-between gap-3 py-2 border-t border-gray-100 first:border-t-0">
            <div className="text-sm">
              <div className="font-medium text-gray-900">{r.name || r.applicationId || 'Candidate'} <span className="text-gray-500 font-normal">{r.applicationId}</span></div>
              <div className="text-xs text-gray-500">
                Asked {fmt(r.requestedAt)} · {r.source === 'candidate' ? 'by the candidate (WhatsApp code)' : 'recorded by HR'}
                {r.hired ? ' · hired — check with People first' : ''}
                {r.status === 'in_progress' ? ` · erasure under way, still to clear: ${r.pending.join(', ')}` : ''}
              </div>
              {declining === r.id && (
                <div className="flex gap-2 mt-2">
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why — the candidate is owed a reason"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-orange-500" />
                  <button onClick={() => decline(r.id)} disabled={!reason.trim()} className="px-2 py-1 text-xs rounded bg-gray-800 text-white disabled:opacity-50">Decline</button>
                  <button onClick={() => { setDeclining(null); setReason(''); }} className="px-2 py-1 text-xs text-gray-500">Cancel</button>
                </div>
              )}
            </div>
            {r.status === 'open' && declining !== r.id && (
              <button onClick={() => setDeclining(r.id)} className="text-xs text-gray-500 hover:text-gray-700 shrink-0">Decline…</button>
            )}
          </div>
        ))}
        {done.length > 0 && (
          <details className="mt-3">
            <summary className="text-xs text-gray-500 cursor-pointer">Closed requests ({done.length})</summary>
            {done.map((r) => (
              <div key={r.id} className="text-xs text-gray-600 py-1">
                {r.applicationId} · asked {fmt(r.requestedAt)} · {r.status === 'erased' ? 'erased' : `declined: ${r.declineReason}`} by {r.decidedBy} on {fmt(r.decidedAt)}
              </div>
            ))}
          </details>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 p-4 mt-4">
        <div className="font-medium text-gray-900 mb-3">Coming up for removal</div>
        {upcoming.length === 0 && <div className="text-sm text-gray-400 italic">Nobody is on the clock yet.</div>}
        {upcoming.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1 font-medium">Candidate</th>
                <th className="py-1 font-medium">Role</th>
                <th className="py-1 font-medium">Outcome</th>
                <th className="py-1 font-medium">{summary.autoDelete ? 'Removed on' : 'Would be removed on'}</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.map((u) => (
                <tr key={u.id} className="border-t border-gray-100">
                  <td className="py-1.5">{u.name || '—'} <span className="text-xs text-gray-400">{u.applicationId}</span></td>
                  <td className="py-1.5 text-gray-600">{u.role}</td>
                  <td className="py-1.5 text-gray-600">{u.reason}</td>
                  <td className={`py-1.5 ${u.due ? 'text-orange-700 font-medium' : 'text-gray-700'}`}>{fmt(u.removeAfter)}{u.due ? ' (past)' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
